import { useMemo } from 'react';
import { Box, Divider, SxProps } from '@mui/material';
import { PieChart, useItemTooltip } from '@mui/x-charts';

import useChartSettings from 'src/hooks/useChartSettings';
import { ChartPlaceholder } from 'src/components/chart/NoDataPlaceholder';
import { TooltipContainer, TooltipRow } from 'src/components/chart/ChartTooltip';
import SkeletonNoData from 'src/components/skeleton/SkeletonNoData';
import { formatNumber } from 'src/utils/numberFormatter';
import { CharDataMapperConfig, chartDataMapper } from 'src/utils/chartDataHelper';

//
//

const CustomItemTooltipContent = (props: any) => {
  const tooltipData = useItemTooltip();
  if (!tooltipData) return null;

  const currentItem = props[tooltipData.identifier.dataIndex as number];

  return (
    <TooltipContainer>
      <TooltipRow
        label={currentItem?.label}
        currentItem={{ color: tooltipData?.color }}
        showDot
      />
      <Divider sx={{ my: 1 }} />
      <TooltipRow
        label="Share:"
        value={`${currentItem?.percentageOfTransactions}%`}
        currentItem={currentItem}
      />
      <TooltipRow
        label="Passengers:"
        value={currentItem?.numberOfPassengers}
        currentItem={currentItem}
      />
      <TooltipRow
        label="Offset CO₂:"
        value={`${formatNumber(currentItem?.co2AmountKgs?.value)} ${currentItem?.co2AmountKgs?.unit?.toLowerCase()}`}
        currentItem={currentItem}
      />
    </TooltipContainer>
  );
};

interface Props {
  data?: any[];
  isLoading: boolean;
  height?: number;
  sx?: SxProps;
}

const CabinClassBreakdown: React.FC<Props> = ({ data = [], isLoading, sx }) => {
  const noDataText = 'See a breakdown of carbon offsets by cabin class.';

  const classBreakdown = useMemo(() => {
    const column_order = ['ECONOMY', 'PREMIUM_ECONOMY', 'BUSINESS', 'FIRST', 'MIXED'];
    const config: CharDataMapperConfig = {
      arr: data,
      labelKey: 'cabinClass',
      valueKey: 'numberOfTransactions',
      column_order,
    }
    return chartDataMapper(config);
  }, [data]);

  const { pieChartSetting } = useChartSettings(classBreakdown);

  return (
      <SkeletonNoData
        isLoading={isLoading}
        hasData={classBreakdown?.length > 0}
        text={noDataText}
        Placeholder={ChartPlaceholder}
        height={260}
      >
        <Box sx={{ width: '100%', height: '100%', ...sx }}>
          <PieChart
            series={[{ data: classBreakdown, type: 'pie' }]}
            slots={{
              tooltip: () => CustomItemTooltipContent(classBreakdown),
            }}
            {...pieChartSetting}
            height={pieChartSetting?.height}
          />
        </Box>
      </SkeletonNoData>
  );
};

export default CabinClassBreakdown;
